import React, { useState, useEffect, useRef } from 'react';
import { Palette, Info, CheckCircle, RefreshCw, Download, Plus, Paintbrush, Save, Trash2, Edit2, ChevronDown, ChevronUp, Slash } from 'lucide-react';
import { useAppDemo } from '../../context/AppDemoContext';
import { 
    argbFromHex, 
    hexFromArgb, 
    themeFromSourceColor, 
    TonalPalette,
    CorePalette
} from "@material/material-color-utilities";

/**
 * MaterialThemeSlide component for generating and selecting Material Design color palettes 
 * based on brand colors.
 */
const MaterialThemeSlide = ({ registerValidation, organization }) => {
    const { 
        brandData,
        primaryColor,
        secondaryColor,
        selectedLogo,
        setColorSchemes,
        setPrimaryColor,
        setSecondaryColor,
        materialPalettes: contextMaterialPalettes,
        setMaterialPalettes,
        selectedPaletteIndex: contextSelectedPaletteIndex,
        setSelectedPaletteIndex,
        selectedPalette: contextSelectedPalette,
        setSelectedPalette,
        savedCustomPalettes: contextSavedPalettes,
        setSavedCustomPalettes
    } = useAppDemo();

    // Use a ref to track initial load
    const initialLoadRef = useRef(true);

    // State for managing generated Material palettes - initialize with default values
    const [materialPalettes, setLocalMaterialPalettes] = useState([]);
    const [activePaletteIndex, setActivePaletteIndex] = useState(0);
    const [activeScheme, setActiveScheme] = useState('light');
    const [selectedPalette, setLocalSelectedPalette] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    
    // State for custom color selection - initialize with default values
    const [customPrimaryColor, setCustomPrimaryColor] = useState('#1e6a5c');
    const [customSecondaryColor, setCustomSecondaryColor] = useState('#6c2d82');
    const [customTertiaryColor, setCustomTertiaryColor] = useState('#4285f4');
    
    // New state for tracking which colors to use - renamed to avoid confusion with hooks
    const [isSecondaryColorEnabled, setIsSecondaryColorEnabled] = useState(true);
    const [isTertiaryColorEnabled, setIsTertiaryColorEnabled] = useState(true);
    
    // New state for saved palettes
    const [savedPalettes, setSavedPalettes] = useState(contextSavedPalettes || []);
    
    // Remove customPaletteName state and use auto-generated names
    const [editMode, setEditMode] = useState(false);
    const [editPaletteIndex, setEditPaletteIndex] = useState(null);

    // Use a ref to track if component is mounted
    const isMountedRef = useRef(true);
    
    // Add this ref at the component level
    const isFirstLoadFromStorage = useRef(true);
    
    // Cleanup on unmount
    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    // Initialize from context values in a controlled way
    useEffect(() => {
        // Only run once on initial mount
        if (initialLoadRef.current) {
            initialLoadRef.current = false;
            
            // Initialize from context
            if (contextMaterialPalettes && contextMaterialPalettes.length > 0) {
                setLocalMaterialPalettes(contextMaterialPalettes);
            }
            
            if (contextSelectedPaletteIndex !== undefined) {
                setActivePaletteIndex(contextSelectedPaletteIndex);
            }
            
            if (contextSelectedPalette) {
                setLocalSelectedPalette(contextSelectedPalette);
            }
            
            if (primaryColor) {
                setCustomPrimaryColor(primaryColor);
            }
            
            if (secondaryColor) {
                setCustomSecondaryColor(secondaryColor);
            }
        }
    }, []);

    // Effect to load saved palettes from localStorage
    useEffect(() => {
        // Remove any hook calls from inside the loadSavedPalettes function
        const loadSavedPalettes = () => {
            if (!isFirstLoadFromStorage.current) {
                return;
            }
            
            isFirstLoadFromStorage.current = false;
            
            try {
                const savedData = localStorage.getItem('savedMaterialPalettes');
                if (savedData) {
                    const parsed = JSON.parse(savedData);
                    
                    // Update local state on initialization
                    setSavedPalettes(parsed);
                    
                    // Only sync with context on initialization if different
                    if (setSavedCustomPalettes && 
                        JSON.stringify(parsed) !== JSON.stringify(contextSavedPalettes)) {
                        setSavedCustomPalettes(parsed);
                    }
                } else if (contextSavedPalettes && contextSavedPalettes.length > 0) {
                    // If no localStorage data but context has data, use context data
                    setSavedPalettes(contextSavedPalettes);
                }
            } catch (error) {
                console.error("Error loading saved palettes:", error);
            }
        };
        
        // Call the function without any hooks inside it
        loadSavedPalettes();
    }, [contextSavedPalettes, setSavedCustomPalettes]);

    // Save palettes to localStorage and context when they change
    useEffect(() => {
        // Skip initial render
        if (initialLoadRef.current) {
            return;
        }
        
        try {
            localStorage.setItem('savedMaterialPalettes', JSON.stringify(savedPalettes));
            
            // Don't update context here to avoid loops - that's handled directly in handleSavePalette
        } catch (error) {
            console.error("Error saving palettes:", error);
        }
    }, [savedPalettes]);

    // Effect to generate material palettes
    useEffect(() => {
        // If we already have material palettes in context, use those
        if (contextMaterialPalettes && contextMaterialPalettes.length > 0) {
            // Start with saved palettes at the TOP, then add context palettes
            let combinedPalettes = [];
            
            // First add saved palettes to ensure they're at the TOP
            if (savedPalettes && savedPalettes.length > 0) {
                // Add all saved palettes first (they go at the TOP)
                savedPalettes.forEach(savedPalette => {
                    combinedPalettes.push({
                        ...savedPalette,
                        isCustom: true // Mark as custom to enable edit/delete
                    });
                });
            }
            
            // Then add context palettes that aren't duplicates of saved ones
            contextMaterialPalettes.forEach(contextPalette => {
                // Check if this context palette is already in our combined list (from saved palettes)
                const isDuplicate = combinedPalettes.some(p => 
                    p.name === contextPalette.name && 
                    p.primaryColor === contextPalette.primaryColor);
                
                if (!isDuplicate) {
                    combinedPalettes.push(contextPalette);
                }
            });
            
            setLocalMaterialPalettes(combinedPalettes);
            
            if (contextSelectedPalette) {
                setLocalSelectedPalette(contextSelectedPalette);
                setActivePaletteIndex(contextSelectedPaletteIndex || 0);
            } else if (combinedPalettes.length > 0) {
                setLocalSelectedPalette(combinedPalettes[0]);
                setActivePaletteIndex(0);
            }
            setIsLoading(false);
            
            // If we have no saved palettes yet, save the generated ones
            if (savedPalettes.length === 0 && setSavedCustomPalettes) {
                // Prepare palettes for saving by adding necessary properties
                const palettesToSave = contextMaterialPalettes.map(palette => ({
                    ...palette,
                    primaryColor: palette.sourceColor,
                    secondaryColor: palette.lightScheme.secondary,
                    tertiaryColor: palette.lightScheme.tertiary,
                    name: palette.name || `Material ${palette.index !== undefined ? palette.index + 1 : ''} (${palette.sourceColor})`,
                    useSecondary: true,
                    useTertiary: true,
                    timestamp: new Date().toISOString()
                }));
                
                setSavedPalettes(palettesToSave);
                setSavedCustomPalettes(palettesToSave);
            }
            return;
        }

        const generateMaterialPalettes = () => {
            setIsLoading(true);
            
            // Get brand colors to use as source colors
            let sourceColors = [];
            
            // Add primary and secondary colors from context if available
            if (primaryColor && primaryColor !== "#1e9963" && primaryColor !== "#3b82f6") {
                sourceColors.push(primaryColor);
            }
            if (secondaryColor && secondaryColor !== "#1e9963" && secondaryColor !== "#3b82f6" && 
                secondaryColor !== primaryColor) { // Avoid duplicate colors
                sourceColors.push(secondaryColor);
            }
            
            // Add colors from brandData if available
            if (brandData?.colors && brandData.colors.length > 0) {
                brandData.colors.forEach(color => {
                    if (color.hex && !sourceColors.includes(color.hex)) {
                        sourceColors.push(color.hex);
                    }
                });
            }
            
            // Fallback to default colors if none are available
            if (sourceColors.length === 0) {
                sourceColors.push('#1e6a5c', '#6c2d82', '#4285f4');
            }
            
            // Ensure unique colors to avoid duplicate palettes
            sourceColors = [...new Set(sourceColors)];
            
            // Generate a material palette for each source color
            const palettes = sourceColors
                .map((color, index) => {
                    const palette = generateMaterialPalette(color);
                    if (palette) {
                        // Add an index to make palette names unique
                        palette.index = index;
                    }
                    return palette;
                })
                .filter(palette => palette !== null);
            
            // Always add a custom palette based on the current color selections
            // Use primary color as fallback for disabled colors
            const secondaryHex = isSecondaryColorEnabled ? customSecondaryColor : customPrimaryColor;
            const tertiaryHex = isTertiaryColorEnabled ? customTertiaryColor : customPrimaryColor;
            
            const customPalette = generateCustomMaterialPalette(
                customPrimaryColor, 
                secondaryHex, 
                tertiaryHex
            );
            
            if (customPalette) {
                // Add color flags to the palette
                customPalette.useSecondary = isSecondaryColorEnabled;
                customPalette.useTertiary = isTertiaryColorEnabled;
                customPalette.primaryColor = customPrimaryColor;
                customPalette.secondaryColor = secondaryHex;
                customPalette.tertiaryColor = tertiaryHex;
                customPalette.index = -1; // Special index for custom palette
                customPalette.isCustom = true;
                
                // Add to beginning of the array so it's the first option
                palettes.unshift(customPalette);
            }
            
            // Start with saved palettes at the TOP, then add generated palettes
            let combinedPalettes = [];
            
            // First add saved palettes to ensure they're at the TOP
            if (savedPalettes && savedPalettes.length > 0) {
                // Add all saved palettes first (they go at the TOP)
                savedPalettes.forEach(savedPalette => {
                    combinedPalettes.push({
                        ...savedPalette,
                        isCustom: true // Mark as custom to enable edit/delete
                    });
                });
            }
            
            // Then add generated palettes that aren't duplicates
            palettes.forEach(generatedPalette => {
                // Check if this generated palette is already in our combined list
                const isDuplicate = combinedPalettes.some(p => 
                    p.isCustom && 
                    p.primaryColor === generatedPalette.primaryColor &&
                    p.secondaryColor === generatedPalette.secondaryColor &&
                    p.tertiaryColor === generatedPalette.tertiaryColor);
                
                if (!isDuplicate) {
                    combinedPalettes.push(generatedPalette);
                }
            });
            
            setLocalMaterialPalettes(combinedPalettes);
            
            // Set first palette as selected by default
            if (combinedPalettes.length > 0 && !selectedPalette) {
                setLocalSelectedPalette(combinedPalettes[0]);
                setActivePaletteIndex(0);
            }
            
            setIsLoading(false);
        };
        
        generateMaterialPalettes();
    }, [brandData, primaryColor, secondaryColor, customPrimaryColor, customSecondaryColor, customTertiaryColor, savedPalettes]);

    // Sync selected palette with context in a controlled way
    useEffect(() => {
        // Skip if we're still in initialization or no palette is selected
        if (initialLoadRef.current || !selectedPalette) {
            return;
        }
        
        // Update palette context if needed - do deep comparison
        if (setSelectedPalette && (!contextSelectedPalette || 
            JSON.stringify(contextSelectedPalette) !== JSON.stringify(selectedPalette))) {
            setSelectedPalette(selectedPalette);
        }
        
        // Only update index if it's different and we have a valid setter
        if (setSelectedPaletteIndex !== undefined && 
            contextSelectedPaletteIndex !== activePaletteIndex) {
            setSelectedPaletteIndex(activePaletteIndex);
        }
    }, [selectedPalette, activePaletteIndex, contextSelectedPalette, 
        contextSelectedPaletteIndex, setSelectedPalette, setSelectedPaletteIndex]);
    
    // Sync material palettes with context in a controlled way
    useEffect(() => {
        // Skip initial render and avoid unnecessary updates
        if (initialLoadRef.current) {
            return;
        }
        
        // Only update context if the values differ and we have palettes to sync
        if (setMaterialPalettes && materialPalettes.length > 0 && 
            (!contextMaterialPalettes || 
             contextMaterialPalettes.length !== materialPalettes.length ||
             JSON.stringify(contextMaterialPalettes) !== JSON.stringify(materialPalettes))) {
            
            // Use a deep comparison to avoid unnecessary updates
            const needsUpdate = !contextMaterialPalettes || 
                               contextMaterialPalettes.length !== materialPalettes.length || 
                               JSON.stringify(contextMaterialPalettes) !== JSON.stringify(materialPalettes);
                               
            if (needsUpdate) {
                setMaterialPalettes(materialPalettes);
            }
        }
    }, [materialPalettes, contextMaterialPalettes, setMaterialPalettes]);

    // Update primary and secondary colors in context when selected palette changes
    useEffect(() => {
        // Skip if not initialized or no selected palette
        if (!initialLoadRef.current || !selectedPalette || !selectedPalette.lightScheme) return;
        
        // Only update if colors are different
        if (setPrimaryColor && selectedPalette.lightScheme.primary && 
            primaryColor !== selectedPalette.lightScheme.primary) {
            setPrimaryColor(selectedPalette.lightScheme.primary);
        }
        
        if (setSecondaryColor && selectedPalette.lightScheme.secondary && 
            secondaryColor !== selectedPalette.lightScheme.secondary) {
            setSecondaryColor(selectedPalette.lightScheme.secondary);
        }
    }, [selectedPalette]);

    // Generate Material Design color palettes from brand colors
    const generateMaterialPalettes = () => {
        setIsLoading(true);
        
        // Get brand colors to use as source colors
        let sourceColors = [];
        
        // Add primary and secondary colors from context if available
        if (primaryColor && primaryColor !== "#1e9963" && primaryColor !== "#3b82f6") {
            sourceColors.push(primaryColor);
        }
        if (secondaryColor && secondaryColor !== "#1e9963" && secondaryColor !== "#3b82f6" && 
            secondaryColor !== primaryColor) { // Avoid duplicate colors
            sourceColors.push(secondaryColor);
        }
        
        // Add colors from brandData if available
        if (brandData?.colors && brandData.colors.length > 0) {
            brandData.colors.forEach(color => {
                if (color.hex && !sourceColors.includes(color.hex)) {
                    sourceColors.push(color.hex);
                }
            });
        }
        
        // Fallback to default colors if none are available
        if (sourceColors.length === 0) {
            sourceColors.push('#1e6a5c', '#6c2d82', '#4285f4');
        }
        
        // Ensure unique colors to avoid duplicate palettes
        sourceColors = [...new Set(sourceColors)];
        
        // Generate a material palette for each source color
        const palettes = sourceColors
            .map((color, index) => {
                const palette = generateMaterialPalette(color);
                if (palette) {
                    // Add an index to make palette names unique
                    palette.index = index;
                }
                return palette;
            })
            .filter(palette => palette !== null);
        
        // Always add a custom palette based on the current color selections
        // Use primary color as fallback for disabled colors
        const secondaryHex = isSecondaryColorEnabled ? customSecondaryColor : customPrimaryColor;
        const tertiaryHex = isTertiaryColorEnabled ? customTertiaryColor : customPrimaryColor;
        
        const customPalette = generateCustomMaterialPalette(
            customPrimaryColor, 
            secondaryHex, 
            tertiaryHex
        );
        
        if (customPalette) {
            // Add color flags to the palette
            customPalette.useSecondary = isSecondaryColorEnabled;
            customPalette.useTertiary = isTertiaryColorEnabled;
            customPalette.primaryColor = customPrimaryColor;
            customPalette.secondaryColor = secondaryHex;
            customPalette.tertiaryColor = tertiaryHex;
            customPalette.index = -1; // Special index for custom palette
            customPalette.isCustom = true;
            
            // Add to beginning of the array so it's the first option
            palettes.unshift(customPalette);
        }
        
        // Start with saved palettes at the TOP, then add generated palettes
        let combinedPalettes = [];
        
        // First add saved palettes to ensure they're at the TOP
        if (savedPalettes && savedPalettes.length > 0) {
            // Add all saved palettes first (they go at the TOP)
            savedPalettes.forEach(savedPalette => {
                combinedPalettes.push({
                    ...savedPalette,
                    isCustom: true // Mark as custom to enable edit/delete
                });
            });
        }
        
        // Then add generated palettes that aren't duplicates
        palettes.forEach(generatedPalette => {
            // Check if this generated palette is already in our combined list
            const isDuplicate = combinedPalettes.some(p => 
                p.isCustom && 
                p.primaryColor === generatedPalette.primaryColor &&
                p.secondaryColor === generatedPalette.secondaryColor &&
                p.tertiaryColor === generatedPalette.tertiaryColor);
            
            if (!isDuplicate) {
                combinedPalettes.push(generatedPalette);
            }
        });
        
        setLocalMaterialPalettes(combinedPalettes);
        
        // Set first palette as selected by default
        if (combinedPalettes.length > 0 && !selectedPalette) {
            setLocalSelectedPalette(combinedPalettes[0]);
            setActivePaletteIndex(0);
        }
        
        setIsLoading(false);
    };

    // Generate a custom Material Design palette with specific primary, secondary, and tertiary colors
    const generateCustomMaterialPalette = (primaryHex, secondaryHex, tertiaryHex) => {
        try {
            // Defensive programming: ensure we have valid hex colors
            if (!primaryHex || !primaryHex.startsWith('#')) {
                console.error("Invalid primary color for material palette generation");
                return null;
            }
            
            // Use primary color as fallback for secondary and tertiary if they're null or invalid
            const validSecondaryHex = (!secondaryHex || !secondaryHex.startsWith('#')) ? primaryHex : secondaryHex;
            const validTertiaryHex = (!tertiaryHex || !tertiaryHex.startsWith('#')) ? primaryHex : tertiaryHex;
            
            // Convert hex colors to ARGB
            const primaryArgb = argbFromHex(primaryHex);
            const secondaryArgb = argbFromHex(validSecondaryHex);
            const tertiaryArgb = argbFromHex(validTertiaryHex);
            
            // Create a complete theme based on the primary color
            const baseTheme = themeFromSourceColor(primaryArgb, []);
            
            // Generate individual tonal palettes for each color
            const primaryTonalPalette = TonalPalette.fromInt(primaryArgb);
            const secondaryTonalPalette = TonalPalette.fromInt(secondaryArgb);
            const tertiaryTonalPalette = TonalPalette.fromInt(tertiaryArgb);
            
            // Generate tones for the palette display (0-100 in steps of 10)
            const tones = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 99, 100];
            
            // Create tonal palette colors for display (using primary color)
            const tonalColors = tones.map(tone => ({
                tone,
                hex: hexFromArgb(primaryTonalPalette.tone(tone))
            }));
            
            // Create custom light scheme
            const lightScheme = {
                // Primary colors
                primary: hexFromArgb(primaryTonalPalette.tone(40)),
                onPrimary: hexFromArgb(primaryTonalPalette.tone(100)),
                primaryContainer: hexFromArgb(primaryTonalPalette.tone(90)),
                onPrimaryContainer: hexFromArgb(primaryTonalPalette.tone(10)),
                
                // Secondary colors
                secondary: hexFromArgb(secondaryTonalPalette.tone(40)),
                onSecondary: hexFromArgb(secondaryTonalPalette.tone(100)),
                secondaryContainer: hexFromArgb(secondaryTonalPalette.tone(90)),
                onSecondaryContainer: hexFromArgb(secondaryTonalPalette.tone(10)),
                
                // Tertiary colors
                tertiary: hexFromArgb(tertiaryTonalPalette.tone(40)),
                onTertiary: hexFromArgb(tertiaryTonalPalette.tone(100)),
                tertiaryContainer: hexFromArgb(tertiaryTonalPalette.tone(90)),
                onTertiaryContainer: hexFromArgb(tertiaryTonalPalette.tone(10)),
                
                // Use base theme for other colors
                surface: hexFromArgb(baseTheme.palettes.neutral.tone(99)),
                onSurface: hexFromArgb(baseTheme.palettes.neutral.tone(10)),
                surfaceVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(90)),
                onSurfaceVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(30)),
                outline: hexFromArgb(baseTheme.palettes.neutralVariant.tone(50)),
                outlineVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(80)),
            };
            
            // Create custom dark scheme
            const darkScheme = {
                // Primary colors
                primary: hexFromArgb(primaryTonalPalette.tone(80)),
                onPrimary: hexFromArgb(primaryTonalPalette.tone(20)),
                primaryContainer: hexFromArgb(primaryTonalPalette.tone(30)),
                onPrimaryContainer: hexFromArgb(primaryTonalPalette.tone(90)),
                
                // Secondary colors
                secondary: hexFromArgb(secondaryTonalPalette.tone(80)),
                onSecondary: hexFromArgb(secondaryTonalPalette.tone(20)),
                secondaryContainer: hexFromArgb(secondaryTonalPalette.tone(30)),
                onSecondaryContainer: hexFromArgb(secondaryTonalPalette.tone(90)),
                
                // Tertiary colors
                tertiary: hexFromArgb(tertiaryTonalPalette.tone(80)),
                onTertiary: hexFromArgb(tertiaryTonalPalette.tone(20)),
                tertiaryContainer: hexFromArgb(tertiaryTonalPalette.tone(30)),
                onTertiaryContainer: hexFromArgb(tertiaryTonalPalette.tone(90)),
                
                // Use base theme for other colors
                surface: hexFromArgb(baseTheme.palettes.neutral.tone(10)),
                onSurface: hexFromArgb(baseTheme.palettes.neutral.tone(90)),
                surfaceVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(30)),
                onSurfaceVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(80)),
                outline: hexFromArgb(baseTheme.palettes.neutralVariant.tone(60)),
                outlineVariant: hexFromArgb(baseTheme.palettes.neutralVariant.tone(30)),
            };
            
            return {
                sourceColor: primaryHex,
                isCustom: true,
                primaryColor: primaryHex,
                secondaryColor: secondaryHex,
                tertiaryColor: tertiaryHex,
                tonalPalette: tonalColors,
                lightScheme,
                darkScheme
            };
        } catch (error) {
            console.error("Error generating custom material palette:", error);
            return null;
        }
    };

    // Helper function to generate a Material Design color palette from a hex color
    const generateMaterialPalette = (hexColor) => {
        if (!hexColor || !hexColor.startsWith('#')) {
            return null;
        }

        try {
            // Convert hex color to ARGB
            const argb = argbFromHex(hexColor);
            
            // Generate theme from source color
            const theme = themeFromSourceColor(argb, []);
            
            // Create tonal palette for the source color
            const primaryTonalPalette = TonalPalette.fromInt(argb);
            
            // Generate tones for the palette (0-100 in steps of 10)
            const tones = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 95, 99, 100];
            
            // Create tonal palette colors
            const tonalColors = tones.map(tone => ({
                tone,
                hex: hexFromArgb(primaryTonalPalette.tone(tone))
            }));
            
            // Get light and dark scheme colors
            const lightScheme = {
                primary: hexFromArgb(theme.schemes.light.primary),
                onPrimary: hexFromArgb(theme.schemes.light.onPrimary),
                primaryContainer: hexFromArgb(theme.schemes.light.primaryContainer),
                onPrimaryContainer: hexFromArgb(theme.schemes.light.onPrimaryContainer),
                secondary: hexFromArgb(theme.schemes.light.secondary),
                onSecondary: hexFromArgb(theme.schemes.light.onSecondary),
                secondaryContainer: hexFromArgb(theme.schemes.light.secondaryContainer),
                onSecondaryContainer: hexFromArgb(theme.schemes.light.onSecondaryContainer),
                tertiary: hexFromArgb(theme.schemes.light.tertiary),
                onTertiary: hexFromArgb(theme.schemes.light.onTertiary),
                tertiaryContainer: hexFromArgb(theme.schemes.light.tertiaryContainer),
                onTertiaryContainer: hexFromArgb(theme.schemes.light.onTertiaryContainer),
                surface: hexFromArgb(theme.schemes.light.surface),
                onSurface: hexFromArgb(theme.schemes.light.onSurface),
            };
            
            const darkScheme = {
                primary: hexFromArgb(theme.schemes.dark.primary),
                onPrimary: hexFromArgb(theme.schemes.dark.onPrimary),
                primaryContainer: hexFromArgb(theme.schemes.dark.primaryContainer),
                onPrimaryContainer: hexFromArgb(theme.schemes.dark.onPrimaryContainer),
                secondary: hexFromArgb(theme.schemes.dark.secondary),
                onSecondary: hexFromArgb(theme.schemes.dark.onSecondary),
                secondaryContainer: hexFromArgb(theme.schemes.dark.secondaryContainer),
                onSecondaryContainer: hexFromArgb(theme.schemes.dark.onSecondaryContainer),
                tertiary: hexFromArgb(theme.schemes.dark.tertiary),
                onTertiary: hexFromArgb(theme.schemes.dark.onTertiary),
                tertiaryContainer: hexFromArgb(theme.schemes.dark.tertiaryContainer),
                onTertiaryContainer: hexFromArgb(theme.schemes.dark.onTertiaryContainer),
                surface: hexFromArgb(theme.schemes.dark.surface),
                onSurface: hexFromArgb(theme.schemes.dark.onSurface),
            };
            
            return {
                sourceColor: hexColor,
                tonalPalette: tonalColors,
                lightScheme,
                darkScheme
            };
        } catch (error) {
            console.error("Error generating material palette:", error);
            return null;
        }
    };

    // Handle selecting a palette
    const handleSelectPalette = (palette, index) => {
        setLocalSelectedPalette(palette);
        setActivePaletteIndex(index);
        
        // Update context
        if (setSelectedPalette) {
            setSelectedPalette(palette);
        }
        if (setSelectedPaletteIndex !== undefined) {
            setSelectedPaletteIndex(index);
        }
        
        // Add to color schemes in app context
        const newColorScheme = {
            name: palette.isCustom 
                ? "Custom Material" 
                : `Material ${palette.index !== undefined ? palette.index + 1 : index + 1} (${palette.sourceColor})`,
            primary_color: palette.lightScheme.primary,
            secondary_color: palette.lightScheme.secondary,
            accent_color: palette.lightScheme.tertiary,
            background_color: palette.lightScheme.surface,
            text_color: palette.lightScheme.onSurface,
            description: palette.isCustom 
                ? `Custom palette with primary: ${palette.primaryColor}, secondary: ${palette.secondaryColor}, tertiary: ${palette.tertiaryColor}`
                : `Generated from ${palette.sourceColor} using Material Design guidelines`
        };
        
        // Update color schemes in context
        setColorSchemes(prevSchemes => {
            // Check if we already have this scheme
            const existingSchemeIndex = prevSchemes.findIndex(
                scheme => scheme.primary_color === newColorScheme.primary_color
            );
            
            if (existingSchemeIndex >= 0) {
                // Replace the existing scheme
                const updatedSchemes = [...prevSchemes];
                updatedSchemes[existingSchemeIndex] = newColorScheme;
                return updatedSchemes;
            } else {
                // Add the new scheme at the beginning
                return [newColorScheme, ...prevSchemes];
            }
        });
        
        // Update primary and secondary colors in context
        if (setPrimaryColor && palette.lightScheme?.primary) {
            setPrimaryColor(palette.lightScheme.primary);
        }
        if (setSecondaryColor && palette.lightScheme?.secondary) {
            setSecondaryColor(palette.lightScheme.secondary);
        }
    };

    // Handle custom palette generation with auto-save
    const handleGenerateCustomPalette = () => {
        setIsLoading(true);
        
        // If colors are disabled, use the primary color as a fallback instead of null
        // This prevents the argbFromHex function from receiving null values
        const secondaryHex = isSecondaryColorEnabled ? customSecondaryColor : customPrimaryColor;
        const tertiaryHex = isTertiaryColorEnabled ? customTertiaryColor : customPrimaryColor;
        
        // Generate custom palette
        const customPalette = generateCustomMaterialPalette(
            customPrimaryColor, 
            secondaryHex, 
            tertiaryHex
        );
        
        if (customPalette) {
            // Generate a name based on the colors
            const paletteName = `Custom Material (${customPrimaryColor})`;
            
            // Add name and source colors to the palette
            customPalette.name = paletteName;
            customPalette.primaryColor = customPrimaryColor;
            customPalette.secondaryColor = secondaryHex;
            customPalette.tertiaryColor = tertiaryHex;
            customPalette.isCustom = true;
            customPalette.useSecondary = isSecondaryColorEnabled;
            customPalette.useTertiary = isTertiaryColorEnabled;
            customPalette.timestamp = new Date().toISOString();
            
            // Make sure new palettes are always added to the TOP
            // Filter out any previous custom palettes with same properties to avoid duplicates
            const filteredPalettes = materialPalettes.filter(p => 
                !(p.isCustom && p.primaryColor === customPrimaryColor && 
                  p.secondaryColor === secondaryHex && p.tertiaryColor === tertiaryHex)
            );
            
            // Add custom palette to the beginning (TOP) of the array
            const updatedPalettes = [customPalette, ...filteredPalettes];
            setLocalMaterialPalettes(updatedPalettes);
            
            // Select the custom palette
            setLocalSelectedPalette(customPalette);
            setActivePaletteIndex(0);
            
            // Automatically save the new palette - add to TOP of saved palettes
            const updatedSavedPalettes = [customPalette, ...savedPalettes.filter(p => 
                !(p.primaryColor === customPrimaryColor && 
                  p.secondaryColor === secondaryHex && 
                  p.tertiaryColor === tertiaryHex)
            )];
            setSavedPalettes(updatedSavedPalettes);
            
            // Update context directly
            if (setSavedCustomPalettes) {
                setSavedCustomPalettes(updatedSavedPalettes);
            }
        }
        
        setIsLoading(false);
    };
    
    // Update the save palette function to use auto-generated names
    const handleSavePalette = () => {
        if (!selectedPalette) return;
        
        // Generate a name based on the colors
        const paletteName = `Custom Material (${customPrimaryColor})`;
        
        const paletteToSave = {
            ...selectedPalette,
            name: paletteName,
            primaryColor: customPrimaryColor,
            secondaryColor: isSecondaryColorEnabled ? customSecondaryColor : customPrimaryColor,
            tertiaryColor: isTertiaryColorEnabled ? customTertiaryColor : customPrimaryColor,
            useSecondary: isSecondaryColorEnabled,
            useTertiary: isTertiaryColorEnabled,
            isCustom: true,
            timestamp: new Date().toISOString()
        };
        
        let updatedPalettes;
        
        if (editMode && editPaletteIndex !== null) {
            // Update existing palette
            updatedPalettes = [...savedPalettes];
            updatedPalettes[editPaletteIndex] = paletteToSave;
        } else {
            // Add new palette to the beginning of the array
            updatedPalettes = [paletteToSave, ...savedPalettes];
        }
        
        // Update local state
        setSavedPalettes(updatedPalettes);
        
        // Update context directly with the same value to prevent synchronization loops
        if (setSavedCustomPalettes) {
            setSavedCustomPalettes(updatedPalettes);
        }
        
        // Update the material palettes to include the updated saved palettes
        // This ensures our unified view is immediately updated
        setLocalMaterialPalettes(prevPalettes => {
            const filteredPalettes = prevPalettes.filter(p => 
                // Keep all generated palettes and saved palettes that weren't edited
                !p.isCustom || 
                (editMode && p !== savedPalettes[editPaletteIndex])
            );
            return [paletteToSave, ...filteredPalettes];
        });
        
        // Reset UI state
        setEditMode(false);
        setEditPaletteIndex(null);
    };
    
    // Update load palette to handle auto-generated names
    const handleLoadPalette = (palette, index) => {
        setCustomPrimaryColor(palette.primaryColor);
        setCustomSecondaryColor(palette.secondaryColor || palette.primaryColor);
        setCustomTertiaryColor(palette.tertiaryColor || palette.primaryColor);
        setIsSecondaryColorEnabled(palette.useSecondary !== undefined ? palette.useSecondary : true);
        setIsTertiaryColorEnabled(palette.useTertiary !== undefined ? palette.useTertiary : true);
        
        // Create the palette with appropriate fallbacks for disabled colors
        const loadedPalette = generateCustomMaterialPalette(
            palette.primaryColor,
            // Always use primaryColor as fallback if the color is disabled or not provided
            (palette.useSecondary && palette.secondaryColor) ? palette.secondaryColor : palette.primaryColor,
            (palette.useTertiary && palette.tertiaryColor) ? palette.tertiaryColor : palette.primaryColor
        );
        
        if (loadedPalette) {
            // Add the useSecondary and useTertiary flags to the loaded palette
            loadedPalette.useSecondary = palette.useSecondary !== undefined ? palette.useSecondary : true;
            loadedPalette.useTertiary = palette.useTertiary !== undefined ? palette.useTertiary : true;
            loadedPalette.name = palette.name;
            loadedPalette.isCustom = true;
            loadedPalette.timestamp = new Date().toISOString(); // Add timestamp to treat as a new palette
            
            // Update material palettes - always keep loaded palette at the TOP
            setLocalMaterialPalettes(prevPalettes => {
                // Filter out any matching palette to avoid duplicates
                const filteredPalettes = prevPalettes.filter(p => 
                    !(p.isCustom && p.name === palette.name && 
                      p.primaryColor === palette.primaryColor && 
                      p.timestamp === palette.timestamp)
                );
                
                // Add the loaded palette to the beginning (TOP)
                return [loadedPalette, ...filteredPalettes];
            });
            
            // Select the loaded palette
            setLocalSelectedPalette(loadedPalette);
            setActivePaletteIndex(0);
        }
    };
    
    // Edit a saved palette
    const handleEditPalette = (palette, index) => {
        handleLoadPalette(palette, index);
        setEditMode(true);
        setEditPaletteIndex(index);
    };
    
    // Delete a saved palette
    const handleDeletePalette = (index) => {
        // Validate the index is within bounds
        if (index < 0 || index >= savedPalettes.length || !savedPalettes[index]) {
            console.error("Invalid palette index to delete:", index);
            return;
        }
        
        // Create the updated array without the deleted palette
        const paletteToDelete = savedPalettes[index];
        const updatedPalettes = savedPalettes.filter((_, i) => i !== index);
        
        // Update local state
        setSavedPalettes(updatedPalettes);
        
        // Update context directly with the same value
        if (setSavedCustomPalettes) {
            setSavedCustomPalettes(updatedPalettes);
        }
        
        // Also update the material palettes to remove the deleted palette
        // This ensures our unified view is immediately updated
        setLocalMaterialPalettes(prevPalettes => {
            // Safely filter palettes - adding null checks to prevent errors
            if (!prevPalettes || !paletteToDelete) return prevPalettes || [];
            
            return prevPalettes.filter(p => 
                // Keep all palettes except the one that matches the deleted one
                !p || !p.isCustom || 
                p.name !== paletteToDelete.name || 
                p.primaryColor !== paletteToDelete.primaryColor ||
                p.timestamp !== paletteToDelete.timestamp
            );
        });
        
        // If the deleted palette was selected, select the first available palette
        if (activePaletteIndex === index) {
            if (materialPalettes.length > 0) {
                handleSelectPalette(materialPalettes[0], 0);
            } else {
                setLocalSelectedPalette(null);
                setActivePaletteIndex(0);
            }
        }
    };

    // Render color chip with name
    const renderColorChip = (color, name) => (
        <div 
            className="flex flex-col items-center group relative"
            title={`${name}: ${color}`}
        >
            <div 
                className="w-9 h-9 rounded-full border border-gray-200 shadow-sm hover:scale-110 transition-transform z-10 hover:z-20" 
                style={{ backgroundColor: color }}
            ></div>
            <span className="text-[9px] text-center text-white/70 max-w-[60px] truncate opacity-0 group-hover:opacity-100 absolute -bottom-5 left-1/2 transform -translate-x-1/2 bg-black/70 px-2 py-0.5 rounded whitespace-nowrap transition-opacity">{name}</span>
        </div>
    );
    
    // Render palette preview card - make them more compact
    const renderPalettePreview = (palette, isActive = false, onClick = null) => {
        return (
            <button 
                className={`p-1.5 rounded-lg border transition-all flex-shrink-0 w-[100px] ${
                    isActive ? 'border-yellow-300/70 bg-white/10' : 'border-white/10 bg-white/5 hover:bg-white/10'
                }`}
                onClick={onClick}
            >
                <div className="flex flex-col items-center">
                    {/* Color preview grid showing feel of palette - made smaller */}
                    <div className="w-full h-16 rounded-md border border-white/20 shadow-md mb-1 overflow-hidden">
                        <div className="h-1/2 flex">
                            <div className="w-1/2 h-full" style={{ backgroundColor: palette.lightScheme.primary }}></div>
                            <div className="w-1/2 h-full" style={{ backgroundColor: palette.lightScheme.secondary }}></div>
                        </div>
                        <div className="h-1/2 flex">
                            <div className="w-1/2 h-full" style={{ backgroundColor: palette.lightScheme.tertiary }}></div>
                            <div className="w-1/2 h-full" style={{ backgroundColor: palette.lightScheme.secondaryContainer }}></div>
                        </div>
                    </div>
                    
                    <span className="text-white text-xs font-medium truncate w-full text-center">
                        {palette.name || (palette.isCustom 
                            ? "Custom" 
                            : `Color ${palette.index + 1}`)}
                    </span>
                    <div className="flex items-center justify-center mt-0.5">
                        <div 
                            className="w-2 h-2 rounded-full border border-white/20 mr-1" 
                            style={{ backgroundColor: palette.sourceColor }}
                        ></div>
                        <span className="text-white/60 text-[8px] truncate">{palette.sourceColor}</span>
                    </div>
                    
                    {isActive && (
                        <div className="flex items-center mt-0.5 text-yellow-300">
                            <CheckCircle className="w-2 h-2 mr-0.5" />
                            <span className="text-[8px]">Selected</span>
                        </div>
                    )}
                </div>
            </button>
        );
    };

    // Generate CSS variables from the selected palette
    const generateCSSVariables = () => {
        if (!selectedPalette) return '';
        
        const scheme = activeScheme === 'light' ? selectedPalette.lightScheme : selectedPalette.darkScheme;
        
        return Object.entries(scheme).map(([key, value]) => {
            return `--md-${key}: ${value};`;
        }).join('\n');
    };

    // Register validation with the parent component
    useEffect(() => {
        registerValidation('material-theme', selectedPalette !== null);
    }, [selectedPalette, registerValidation]);

    return (
        <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto">
                {/* Main content container */}
                <div className="max-w-6xl mx-auto pb-8">
                    <div className="mb-6">
                        <div className="flex items-center justify-between">
                            <h2 className="text-2xl font-medium text-white mb-2 flex items-center">
                                <Palette className="mr-2 h-6 w-6 text-yellow-300" />
                                Material Theme
                            </h2>
                            {/* Display brand colors used */}
                            {brandData?.colors && brandData.colors.length > 0 && (
                                <div className="flex items-center">
                                    <span className="text-white/70 text-sm mr-2">Brand Colors:</span>
                                    <div className="flex space-x-1">
                                        {brandData.colors.map((color, index) => (
                                            <div
                                                key={`brand-color-${index}`}
                                                className="w-6 h-6 rounded-full border border-white/20"
                                                style={{ backgroundColor: color.hex }}
                                                title={color.type || `Color ${index + 1}: ${color.hex}`}
                                            />
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                        <p className="text-white/80">
                            Select or create a Material Design 3 color scheme based on your brand colors.
                            The selected theme will be applied to your app.
                        </p>
                    </div>
                    
                    {/* Main two-column layout */}
                    <div className="flex flex-col lg:flex-row gap-4">
                        {/* Left column - smaller for controls */}
                        <div className="w-full lg:w-1/3 lg:overflow-y-auto lg:max-h-[calc(100vh-200px)]">
                            {/* Custom palette editor - always visible but more compact */}
                            <div className="mb-4 bg-green-900/30 rounded-lg border border-white/10 overflow-hidden">
                                <div className="p-3 pb-4">
                                    <div className="flex items-center mb-2">
                                        <h3 className="text-base font-medium text-white flex items-center">
                                            <Paintbrush className="w-4 h-4 text-yellow-300 mr-1.5" />
                                            <span>{editMode ? 'Edit Palette' : 'Create Custom Palette'}</span>
                                        </h3>
                                    </div>
                                    
                                    {/* More compact color selection */}
                                    <div className="grid grid-cols-3 gap-2 mb-3">
                                        {/* Primary Color */}
                                        <div className="bg-black/20 p-2 rounded-md">
                                            <label className="block text-white text-xs font-medium mb-1">Primary</label>
                                            <div className="flex items-center">
                                                <input 
                                                    type="color" 
                                                    value={customPrimaryColor}
                                                    onChange={(e) => setCustomPrimaryColor(e.target.value)}
                                                    className="w-10 h-10 rounded-full cursor-pointer border-2 border-white/30"
                                                />
                                                
                                                {/* Brand colors as horizontal scroll */}
                                                {brandData && brandData.colors && brandData.colors.length > 0 && (
                                                    <div className="ml-1 flex-shrink-0 flex overflow-x-auto">
                                                        {brandData.colors.map((color, index) => (
                                                            <button
                                                                key={`primary-brand-${index}`}
                                                                className={`w-5 h-5 rounded-full border-2 ml-0.5 flex-shrink-0 transition-all ${customPrimaryColor === color.hex ? 'border-yellow-300 scale-110' : 'border-white/20 hover:border-white/50'}`}
                                                                style={{ backgroundColor: color.hex }}
                                                                onClick={() => setCustomPrimaryColor(color.hex)}
                                                                title={color.type || `Brand color ${index + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Secondary Color */}
                                        <div className={`bg-black/20 p-2 rounded-md transition-opacity ${!isSecondaryColorEnabled ? 'opacity-60' : ''}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-white text-xs font-medium">Secondary</label>
                                                {/* Toggle button made smaller */}
                                                {isSecondaryColorEnabled ? (
                                                    <button 
                                                        onClick={() => setIsSecondaryColorEnabled(false)}
                                                        className="px-1 py-0.5 bg-white/10 hover:bg-white/20 text-white text-[10px] rounded"
                                                        title="Disable secondary color"
                                                    >
                                                        Disable
                                                    </button>
                                                ) : (
                                                    <button 
                                                        onClick={() => setIsSecondaryColorEnabled(true)}
                                                        className="px-1 py-0.5 bg-white/10 hover:bg-white/20 text-white text-[10px] rounded"
                                                        title="Enable secondary color"
                                                    >
                                                        Enable
                                                    </button>
                                                )}
                                            </div>
                                            
                                            <div className="flex items-center">
                                                {isSecondaryColorEnabled ? (
                                                    <input 
                                                        type="color" 
                                                        value={customSecondaryColor}
                                                        onChange={(e) => setCustomSecondaryColor(e.target.value)}
                                                        className="w-10 h-10 rounded-full cursor-pointer border-2 border-white/30"
                                                    />
                                                ) : (
                                                    <button 
                                                        className="w-10 h-10 rounded-full border-2 border-white/30 bg-white flex items-center justify-center relative"
                                                        onClick={() => setIsSecondaryColorEnabled(true)}
                                                        title="Enable secondary color"
                                                    >
                                                        <Slash className="w-8 h-8 text-red-500 absolute" />
                                                    </button>
                                                )}
                                                
                                                {/* Brand colors */}
                                                {isSecondaryColorEnabled && brandData && brandData.colors && brandData.colors.length > 0 && (
                                                    <div className="ml-1 flex-shrink-0 flex overflow-x-auto">
                                                        {brandData.colors.map((color, index) => (
                                                            <button
                                                                key={`secondary-brand-${index}`}
                                                                className={`w-5 h-5 rounded-full border-2 ml-0.5 flex-shrink-0 transition-all ${customSecondaryColor === color.hex ? 'border-yellow-300 scale-110' : 'border-white/20 hover:border-white/50'}`}
                                                                style={{ backgroundColor: color.hex }}
                                                                onClick={() => setCustomSecondaryColor(color.hex)}
                                                                title={color.type || `Brand color ${index + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {/* Tertiary Color */}
                                        <div className={`bg-black/20 p-2 rounded-md transition-opacity ${!isTertiaryColorEnabled ? 'opacity-60' : ''}`}>
                                            <div className="flex items-center justify-between mb-1">
                                                <label className="text-white text-xs font-medium">Tertiary</label>
                                                {/* Toggle button made smaller */}
                                                {isTertiaryColorEnabled ? (
                                                    <button 
                                                        onClick={() => setIsTertiaryColorEnabled(false)}
                                                        className="px-1 py-0.5 bg-white/10 hover:bg-white/20 text-white text-[10px] rounded"
                                                        title="Disable tertiary color"
                                                    >
                                                        Disable
                                                    </button>
                                                ) : (
                                                    <button 
                                                        onClick={() => setIsTertiaryColorEnabled(true)}
                                                        className="px-1 py-0.5 bg-white/10 hover:bg-white/20 text-white text-[10px] rounded"
                                                        title="Enable tertiary color"
                                                    >
                                                        Enable
                                                    </button>
                                                )}
                                            </div>
                                            
                                            <div className="flex items-center">
                                                {isTertiaryColorEnabled ? (
                                                    <input 
                                                        type="color" 
                                                        value={customTertiaryColor}
                                                        onChange={(e) => setCustomTertiaryColor(e.target.value)}
                                                        className="w-10 h-10 rounded-full cursor-pointer border-2 border-white/30"
                                                    />
                                                ) : (
                                                    <button 
                                                        className="w-10 h-10 rounded-full border-2 border-white/30 bg-white flex items-center justify-center relative"
                                                        onClick={() => setIsTertiaryColorEnabled(true)}
                                                        title="Enable tertiary color"
                                                    >
                                                        <Slash className="w-8 h-8 text-red-500 absolute" />
                                                    </button>
                                                )}
                                                
                                                {/* Brand colors */}
                                                {isTertiaryColorEnabled && brandData && brandData.colors && brandData.colors.length > 0 && (
                                                    <div className="ml-1 flex-shrink-0 flex overflow-x-auto">
                                                        {brandData.colors.map((color, index) => (
                                                            <button
                                                                key={`tertiary-brand-${index}`}
                                                                className={`w-5 h-5 rounded-full border-2 ml-0.5 flex-shrink-0 transition-all ${customTertiaryColor === color.hex ? 'border-yellow-300 scale-110' : 'border-white/20 hover:border-white/50'}`}
                                                                style={{ backgroundColor: color.hex }}
                                                                onClick={() => setCustomTertiaryColor(color.hex)}
                                                                title={color.type || `Brand color ${index + 1}`}
                                                            />
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="flex flex-col gap-2">
                                        <button 
                                            onClick={handleGenerateCustomPalette}
                                            className="flex-1 px-3 py-1.5 bg-yellow-400/80 hover:bg-yellow-400 text-green-900 font-medium rounded transition-colors flex items-center justify-center"
                                            title="Generate a new palette with the selected colors"
                                        >
                                            <RefreshCw className="w-3.5 h-3.5 mr-1" />
                                            <span>{editMode ? 'Update Palette' : 'Create New Palette'}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>
                            
                            {/* Unified Palettes Section - more compact */}
                            <div className="mb-4">
                                <h3 className="text-base font-medium text-white mb-2 flex items-center">
                                    <Palette className="w-4 h-4 text-yellow-300 mr-1.5" />
                                    My Palettes
                                </h3>
                                
                                <p className="text-white/70 text-xs mb-2">
                                    Click on a palette to preview it in your app.
                                </p>
                                
                                {isLoading ? (
                                    <div className="flex items-center justify-center h-24">
                                        <div className="flex flex-col items-center">
                                            <RefreshCw className="w-6 h-6 text-white/60 animate-spin mb-1" />
                                            <p className="text-white/60 text-sm">Generating palettes...</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-3 gap-1.5">
                                        {materialPalettes.map((palette, index) => (
                                            <div key={`palette-${index}`} className="relative group">
                                                {renderPalettePreview(
                                                    { ...palette, index, name: palette.name || (palette.isCustom ? "Custom" : `Palette ${index + 1}`) },
                                                    activePaletteIndex === index,
                                                    () => handleSelectPalette(palette, index)
                                                )}
                                                
                                                {/* Action buttons overlayed on hover - made smaller */}
                                                <div className="absolute top-0.5 right-0.5 opacity-0 group-hover:opacity-100 transition-opacity flex gap-0.5 bg-black/50 rounded-md p-0.5">
                                                    <button 
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleEditPalette(palette, index);
                                                        }}
                                                        className="p-0.5 text-white/70 hover:text-white bg-black/30 rounded"
                                                        title="Edit this palette"
                                                    >
                                                        <Edit2 className="w-2.5 h-2.5" />
                                                    </button>
                                                    {/* Only show delete button for custom/saved palettes */}
                                                    {palette.isCustom && (
                                                        <button 
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeletePalette(index);
                                                            }}
                                                            className="p-0.5 text-white/70 hover:text-red-400 bg-black/30 rounded"
                                                            title="Delete this palette"
                                                        >
                                                            <Trash2 className="w-2.5 h-2.5" />
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {/* Right column - Preview */}
                        <div className="w-full lg:w-2/3 bg-white/10 backdrop-blur-sm rounded-lg border border-white/10 p-4 flex flex-col lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-100px)]">
                            <div className="flex justify-between items-center mb-4">
                                <h3 className="text-lg font-medium text-white">Preview</h3>
                                
                                {/* Theme toggle */}
                                <div className="bg-white/10 rounded-full p-1 flex items-center">
                                    <button 
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                            activeScheme === 'light' 
                                                ? 'bg-white/20 text-white' 
                                                : 'text-white/60 hover:text-white/80'
                                        }`}
                                        onClick={() => setActiveScheme('light')}
                                    >
                                        Light
                                    </button>
                                    <button 
                                        className={`px-3 py-1 rounded-full text-sm font-medium transition-colors ${
                                            activeScheme === 'dark' 
                                                ? 'bg-white/20 text-white' 
                                                : 'text-white/60 hover:text-white/80'
                                        }`}
                                        onClick={() => setActiveScheme('dark')}
                                    >
                                        Dark
                                    </button>
                                </div>
                            </div>
                            
                            {selectedPalette ? (
                                <div className="flex-1 flex flex-col">
                                    {/* Theme color chips */}
                                    <div className="flex items-center mb-4 overflow-x-auto pb-2">
                                        <div className="flex items-center ml-2">
                                            {Object.entries(
                                                activeScheme === 'light' 
                                                    ? selectedPalette.lightScheme 
                                                    : selectedPalette.darkScheme
                                            ).map(([key, color], index) => (
                                                <div 
                                                    key={`${key}-${index}`}
                                                    className="ml-[-12px] first:ml-0" 
                                                    style={{ zIndex: 30 - index }}
                                                >
                                                    {renderColorChip(color, key)}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    
                                    {/* Mock UI Preview */}
                                    <div className="flex-1 border rounded-lg overflow-hidden" 
                                        style={{ 
                                            backgroundColor: activeScheme === 'light' 
                                                ? selectedPalette.lightScheme.surface 
                                                : selectedPalette.darkScheme.surface 
                                        }}
                                    >
                                        {/* App bar */}
                                        <div 
                                            className="px-4 py-3 flex items-center justify-between" 
                                            style={{ 
                                                backgroundColor: activeScheme === 'light' 
                                                    ? selectedPalette.lightScheme.primary 
                                                    : selectedPalette.darkScheme.primary,
                                                color: activeScheme === 'light' 
                                                    ? selectedPalette.lightScheme.onPrimary 
                                                    : selectedPalette.darkScheme.onPrimary 
                                            }}
                                        >
                                            <div className="font-medium flex items-center">
                                                {selectedLogo?.src ? (
                                                    <img 
                                                        src={selectedLogo.src} 
                                                        alt="Logo" 
                                                        className="h-6 mr-2"
                                                    />
                                                ) : (
                                                    <Palette className="w-5 h-5 mr-2" />
                                                )}
                                                <span>{organization?.name || 'App Name'}</span>
                                            </div>
                                            <div className="flex gap-2">
                                                <div className="w-5 h-5 rounded-full"></div>
                                                <div className="w-5 h-5 rounded-full"></div>
                                            </div>
                                        </div>
                                        
                                        {/* Content */}
                                        <div className="p-6">
                                            <div 
                                                className="mb-6"
                                                style={{ 
                                                    color: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.onSurface 
                                                        : selectedPalette.darkScheme.onSurface
                                                }}
                                            >
                                                <h3 className="text-xl font-medium mb-1">App Heading</h3>
                                                <p>This is how your app will look with the selected Material Design theme.</p>
                                            </div>
                                            
                                            {/* Primary button */}
                                            <button 
                                                className="px-6 py-3 rounded-lg font-medium mb-4 w-full"
                                                style={{ 
                                                    backgroundColor: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.primary 
                                                        : selectedPalette.darkScheme.primary,
                                                    color: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.onPrimary 
                                                        : selectedPalette.darkScheme.onPrimary 
                                                }}
                                            >
                                                Primary Action Button
                                            </button>
                                            
                                            {/* Secondary button */}
                                            <button 
                                                className="px-6 py-3 rounded-lg font-medium mb-6 w-full"
                                                style={{ 
                                                    backgroundColor: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.secondaryContainer 
                                                        : selectedPalette.darkScheme.secondaryContainer,
                                                    color: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.onSecondaryContainer 
                                                        : selectedPalette.darkScheme.onSecondaryContainer 
                                                }}
                                            >
                                                Secondary Action Button
                                            </button>
                                            
                                            {/* Cards */}
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                                {/* Primary container card */}
                                                <div 
                                                    className="rounded-lg p-4"
                                                    style={{ 
                                                        backgroundColor: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.primaryContainer 
                                                            : selectedPalette.darkScheme.primaryContainer,
                                                        color: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.onPrimaryContainer 
                                                            : selectedPalette.darkScheme.onPrimaryContainer 
                                                    }}
                                                >
                                                    <h4 className="font-medium mb-2">Primary Container</h4>
                                                    <p className="text-sm mb-3">Content in primary container.</p>
                                                    <button 
                                                        className="text-sm px-3 py-1 rounded"
                                                        style={{ 
                                                            backgroundColor: activeScheme === 'light' 
                                                                ? selectedPalette.lightScheme.primary 
                                                                : selectedPalette.darkScheme.primary,
                                                            color: activeScheme === 'light' 
                                                                ? selectedPalette.lightScheme.onPrimary 
                                                                : selectedPalette.darkScheme.onPrimary 
                                                        }}
                                                    >
                                                        Button
                                                    </button>
                                                </div>
                                                
                                                {/* Secondary container card */}
                                                <div 
                                                    className="rounded-lg p-4"
                                                    style={{ 
                                                        backgroundColor: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.secondaryContainer 
                                                            : selectedPalette.darkScheme.secondaryContainer,
                                                        color: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.onSecondaryContainer 
                                                            : selectedPalette.darkScheme.onSecondaryContainer 
                                                    }}
                                                >
                                                    <h4 className="font-medium mb-2">Secondary Container</h4>
                                                    <p className="text-sm mb-3">Content in secondary container.</p>
                                                    <button 
                                                        className="text-sm px-3 py-1 rounded"
                                                        style={{ 
                                                            backgroundColor: activeScheme === 'light' 
                                                                ? selectedPalette.lightScheme.secondary 
                                                                : selectedPalette.darkScheme.secondary,
                                                            color: activeScheme === 'light' 
                                                                ? selectedPalette.lightScheme.onSecondary 
                                                                : selectedPalette.darkScheme.onSecondary 
                                                        }}
                                                    >
                                                        Button
                                                    </button>
                                                </div>
                                            </div>
                                            
                                            {/* Tertiary container */}
                                            <div 
                                                className="rounded-lg p-4 mb-4"
                                                style={{ 
                                                    backgroundColor: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.tertiaryContainer 
                                                        : selectedPalette.darkScheme.tertiaryContainer,
                                                    color: activeScheme === 'light' 
                                                        ? selectedPalette.lightScheme.onTertiaryContainer 
                                                        : selectedPalette.darkScheme.onTertiaryContainer 
                                                }}
                                            >
                                                <div className="flex items-start">
                                                    <Info className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                                                    <div>
                                                        <h4 className="font-medium mb-1">Tertiary Container</h4>
                                                        <p className="text-sm">This container is often used for important notes or callouts in your app interface.</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        {/* Bottom navigation */}
                                        <div 
                                            className="px-4 py-3 flex justify-around border-t"
                                            style={{ 
                                                borderColor: `${selectedPalette.lightScheme.outline}40`,
                                                backgroundColor: activeScheme === 'light' 
                                                    ? selectedPalette.lightScheme.surface
                                                    : selectedPalette.darkScheme.surface,
                                                color: activeScheme === 'light' 
                                                    ? selectedPalette.lightScheme.onSurface
                                                    : selectedPalette.darkScheme.onSurface
                                            }}
                                        >
                                            <div className="flex flex-col items-center">
                                                <div 
                                                    className="w-6 h-6 rounded-full flex items-center justify-center"
                                                    style={{ 
                                                        backgroundColor: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.primary
                                                            : selectedPalette.darkScheme.primary,
                                                        color: activeScheme === 'light' 
                                                            ? selectedPalette.lightScheme.onPrimary
                                                            : selectedPalette.darkScheme.onPrimary
                                                    }}
                                                ></div>
                                                <span className="text-xs mt-1">Home</span>
                                            </div>
                                            <div className="flex flex-col items-center opacity-60">
                                                <div className="w-6 h-6 rounded-full"></div>
                                                <span className="text-xs mt-1">Search</span>
                                            </div>
                                            <div className="flex flex-col items-center opacity-60">
                                                <div className="w-6 h-6 rounded-full"></div>
                                                <span className="text-xs mt-1">Profile</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ) : isLoading ? (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="flex flex-col items-center">
                                        <RefreshCw className="w-8 h-8 text-white/60 animate-spin mb-2" />
                                        <p className="text-white/60">Generating theme preview...</p>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex-1 flex items-center justify-center">
                                    <div className="text-center max-w-md">
                                        <p className="text-white/60 mb-4">Select a color palette from the left panel to preview your Material Design theme.</p>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                
                {/* Info panel - moved outside the columns */}
                <div className="mt-6 p-4 bg-blue-500/20 border border-blue-500/30 rounded-lg text-white">
                    <div className="flex items-start">
                        <Info className="w-5 h-5 text-blue-300 mr-2 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm">Material Design is a design system built and supported by Google that helps teams build high-quality digital experiences. The Material color system provides accessible color schemes while allowing for design flexibility and brand expression.</p>
                            <p className="text-sm mt-2">The color palettes shown here are generated using Google's Material Color Utilities, which creates scientifically derived color schemes that maintain proper contrast and accessibility.</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MaterialThemeSlide; 